import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { payoutApi } from "services/FinanceService";

const FinanceContext = createContext(null);
export const useFinance = () => useContext(FinanceContext);
export function FinanceProvider({ children }) {
  const { token, loading, hasAccess, logout } = useAuth();
  const allowed = !loading && Boolean(token) && hasAccess("finance");
  const { pathname } = useLocation();
  const [queue, setQueue] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const request = useRef(null);
  const refresh = useCallback(async () => {
    if (!allowed || document.visibilityState === "hidden") return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setBusy(true);
    try {
      const data = await payoutApi("/pending", {
        token,
        signal: controller.signal,
      });
      if (!controller.signal.aborted) {
        setQueue(Array.isArray(data) ? data : []);
        setError(null);
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setError(err.message);
        if (err.status === 401) logout();
      }
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  }, [allowed, token, logout]);
  useEffect(() => {
    setQueue([]);
    setError(null);
    setBusy(false);
    if (!allowed) return undefined;
    refresh();
    const timer = setInterval(refresh, 60000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
      request.current?.abort();
    };
  }, [allowed, refresh]);
  useEffect(() => {
    if (pathname === "/payout-approvals") refresh();
  }, [pathname, refresh]);
  return (
    <FinanceContext.Provider value={{ queue, error, busy, refresh }}>
      {children}
    </FinanceContext.Provider>
  );
}
